import React from "react";
import { Platform } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { SystemBars } from "react-native-edge-to-edge";
import { ApolloProvider } from "@apollo/client/react";
import "./configs/sentry.config";
import "./configs/mixpanel";
import client from "./configs/graphql.config";
import RootNavigation from "./navigations/RootNavigation";
import FirebaseManager from "./contexts/FirebaseManager";
import AuthManager from "./contexts/AuthManager";
import SafeAreaManager from "./contexts/SafeAreaManager";
import { EventProvider } from "react-native-outside-press";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import Constants from "expo-constants";
import DropdownManager from "./contexts/DropdownManager";
import SnackBarPortal from "./components/SnackbarZone";
import TransparentOverlayPortal from "./components/TransparentOverlayPortal";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import NetworkManager from "./contexts/NetworkManager";
import "./image-cache";
import { IntercomProvider } from "./contexts/IntercomProvider";
import * as SplashScreen from "expo-splash-screen";
import ScreenHeaderManager from "./contexts/ScreenHeaderManager";
import ProfileSwitcherManager from "./contexts/ProfileSwitcherManager";
import { ImageCropZoomPortal } from "./components/cropZoom/ImageCropZoomModal";
import { useAtomValue } from "jotai";
import { addStorybookToDevMenu, storybookModeAtom } from "./storybook";
import StepsManager from "./contexts/StepsManager";
import { theme } from "./theme";
import { ThemeProvider } from "./contexts/ThemeManager";
import { IntercomButton } from "./components/IntercomButton";
import ImpersonationBar from "./components/ImpersonationBar";
import AlertProvider from "./contexts/AlertProvider";
import RecommendedBrowserWarningBar from "./components/RecommendedBrowserWarningBar";
import DocumentManager from "./contexts/DocumentManager";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import KeyboardStickyComponentManager from "./contexts/KeyboardStickyComponentManager";
import { KeyboardAvoidingView, KeyboardProvider } from "react-native-keyboard-controller";
import ErrorBoundary from "./ui_atoms/ErrorBoundary";
import MaintenanceManager from "./contexts/MaintenanceManager";

if (Platform.OS === "web") {
    require("../global.css");
}

addStorybookToDevMenu();

/**
 * Prevent the splash screen from auto hiding,
 * hide is called in AuthManager AND in the App if we are in storybook mode
 */
// eslint-disable-next-line @typescript-eslint/no-floating-promises
SplashScreen.preventAutoHideAsync().then((result) => {
    // used to dbug some problem on some android phones
    // eslint-disable-next-line no-console
    console.log("preventAutoHideAsync", result);
});

// eslint-disable-next-line no-console
console.log(
    "App env",
    Constants.expoConfig?.extra?.appEnv,
    "Platform",
    Platform.OS,
    "Version",
    Platform.Version,
    "GitVersion",
    Constants.expoConfig?.extra?.gitVersion,
);

export default function App() {
    const isStorybookMode = useAtomValue(storybookModeAtom);

    if (isStorybookMode || Constants.expoConfig?.extra?.displayStorybook) {
        const StorybookScreen = require("../.ondevice").default;
        void SplashScreen.hideAsync();
        return <StorybookScreen />;
    }

    return (
        <ThemeProvider value={theme}>
            <AlertProvider>
                <IntercomProvider
                    {...(Platform.OS === "web"
                        ? {
                              appId: Constants.expoConfig?.extra?.intercomAppId,
                              autoBoot: true,
                              autoBootProps: {
                                  hideDefaultLauncher: true, // always hide, we use a custom button on web
                              },
                          }
                        : {})}
                >
                    <ApolloProvider client={client}>
                        <FirebaseManager>
                            <ActionSheetProvider>
                                <SafeAreaProvider>
                                    <MaintenanceManager>
                                        <ErrorBoundary>
                                            <RecommendedBrowserWarningBar />
                                            <EventProvider>
                                                <AuthManager>
                                                    <ImpersonationBar />
                                                    <ProfileSwitcherManager>
                                                        <KeyboardProvider>
                                                            <SafeAreaManager>
                                                                <NetworkManager>
                                                                    <GestureHandlerRootView
                                                                        style={{
                                                                            flex: 1,
                                                                            backgroundColor:
                                                                                theme.colors
                                                                                    .background,
                                                                        }}
                                                                    >
                                                                        <KeyboardAvoidingView
                                                                            style={{ flex: 1 }}
                                                                        >
                                                                            <BottomSheetModalProvider>
                                                                                <KeyboardStickyComponentManager>
                                                                                    <DropdownManager>
                                                                                        <ScreenHeaderManager>
                                                                                            <StepsManager>
                                                                                                <DocumentManager>
                                                                                                    <RootNavigation />
                                                                                                </DocumentManager>
                                                                                            </StepsManager>
                                                                                        </ScreenHeaderManager>
                                                                                        <TransparentOverlayPortal />
                                                                                    </DropdownManager>
                                                                                </KeyboardStickyComponentManager>
                                                                            </BottomSheetModalProvider>
                                                                        </KeyboardAvoidingView>
                                                                    </GestureHandlerRootView>
                                                                </NetworkManager>
                                                            </SafeAreaManager>
                                                        </KeyboardProvider>
                                                    </ProfileSwitcherManager>
                                                    <IntercomButton />
                                                </AuthManager>
                                            </EventProvider>
                                            <SnackBarPortal />
                                            <ImageCropZoomPortal />
                                        </ErrorBoundary>
                                    </MaintenanceManager>
                                </SafeAreaProvider>
                            </ActionSheetProvider>
                        </FirebaseManager>
                        <SystemBars style="dark" />
                    </ApolloProvider>
                </IntercomProvider>
            </AlertProvider>
        </ThemeProvider>
    );
}
